import {
  createBrowserRouter,
  RouterProvider,
  Navigate
} from 'react-router';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/sonner';
import { GuestRoute } from '@/components/layouts/guest-route';
import { ProtectedRoute } from '@/components/layouts/protected-route';
import { AuthLayout } from '@/components/layouts/auth-layout';
import { AppShell } from '@/components/layouts/app-shell';
import LoginPage from '@/routes/login';
import AccountsPage from '@/routes/accounts';
import AccountDetailPage from '@/routes/account-detail';
import FeedbacksPage from '@/routes/feedbacks';
import AnalyticsPage from '@/routes/analytics';
import AiCostAccessPage from '@/routes/ai-cost-access';
import { AnalyticsLayout } from '@/components/layouts/analytics-layout';
import { SingleAccountAnalytics } from '@/modules/analytics-v2/components/single-account-analytics';
import SentinelSupportOverviewPage from '@/routes/sentinel-analytics/support-overview';
import SentinelInflowOverviewPage from '@/routes/sentinel-analytics/inflow-overview';
import SentinelPerformancePage from '@/routes/sentinel-analytics/performance';
import SentinelMessageMetricsPage from '@/routes/sentinel-analytics/message-metrics';
import SentinelTicketMetricsPage from '@/routes/sentinel-analytics/ticket-metrics';
import NotFoundPage from '@/routes/not-found';

const router = createBrowserRouter([
  {
    element: <GuestRoute />,
    children: [
      {
        path: '/login',
        element: <AuthLayout />,
        children: [{ index: true, element: <LoginPage /> }]
      }
    ]
  },
  {
    path: '/',
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <Navigate to='/accounts' replace /> },
          { path: 'accounts', element: <AccountsPage /> },
          { path: 'accounts/:accountId', element: <AccountDetailPage /> },
          { path: 'feedbacks', element: <FeedbacksPage /> },
          {
            path: 'analytics',
            element: <AnalyticsLayout />,
            children: [
              { index: true, element: <AnalyticsPage /> },
              { path: 'internal/message-metrics', element: <SingleAccountAnalytics key='message' tab='message' /> },
              { path: 'internal/ticket-metrics', element: <SingleAccountAnalytics key='ticket' tab='ticket' /> },
              { path: 'internal/coverage-funnel', element: <SingleAccountAnalytics key='funnel' tab='funnel' /> },
              { path: 'internal/ai-cost', element: <SingleAccountAnalytics key='cost' tab='cost' /> },
              { path: 'sentinel', element: <Navigate to='support/overview' replace /> },
              { path: 'sentinel/support/overview', element: <SentinelSupportOverviewPage /> },
              { path: 'sentinel/support/performance', element: <SentinelPerformancePage /> },
              { path: 'sentinel/inflow/overview', element: <SentinelInflowOverviewPage /> },
              { path: 'sentinel/inflow/message-metrics', element: <SentinelMessageMetricsPage /> },
              { path: 'sentinel/inflow/ticket-metrics', element: <SentinelTicketMetricsPage /> }
            ]
          },
          // Sentinel Analytics' first home; links shared from shared dev still land.
          { path: 'sentinel-analytics/*', element: <Navigate to='/analytics/sentinel' replace /> },
          // Hidden: reached by URL only, deliberately not in menu.config.
          { path: 'ai-cost-access', element: <AiCostAccessPage /> }
        ]
      }
    ]
  },
  { path: '*', element: <NotFoundPage /> }
]);

export function App() {
  return (
    <TooltipProvider delayDuration={200}>
      <RouterProvider router={router} />
      <Toaster position='top-center' richColors />
    </TooltipProvider>
  );
}
